import React, { useContext, useEffect, useState } from "react";
import AppSettings from "../../../Settings/AppSettings";
import ServiceWrapper from "../../../Services/ServiceWrapper";
import AuthManager from "../../../Common/AuthManager";
import { AppContext } from "../../../Context/AppContext";
import ReversePDW from "../../../Components/GMS/Pdw/ReversePDW";
import PdwDownload from "../../../Components/GMS/Pdw/PdwDownload";
import Create from "../../../Components/GMS/Pdw/upload/Create";
import Update from "../../../Components/GMS/Pdw/upload/Update";
import Validate from "../../../Components/GMS/Pdw/upload/Validate";
import { toast } from "Components/DesignSystem/Toast/useToast";
import Dialog from "Components/DesignSystem/Dialog/Dialog";
import Button from "Components/DesignSystem/Button/Button";
import DropdownButton from "Components/DesignSystem/DropdownButton/DropdownButton";
import Typography from "Components/DesignSystem/Typography/Typography";
import Skeleton from "Components/DesignSystem/Skeleton/Skeleton";
import { useNavigate } from "react-router";
import PDWTracker from "../../../Components/GMS/Pdw/PDWTracker";

/**
 * PDW — the bulk product-data workbook page, on the design system (§ 125).
 *
 * Six `Components/Common/Modal/ModalContainer` stacks became six DS `Dialog`s, the
 * two Bootstrap `data-bs-toggle="dropdown"` menus became `DropdownButton`, and the
 * `float:left`/`float:right` header with its `overflow-hidden` clearfix became a
 * flex row.
 *
 * **Every job-polling and permission behaviour is untouched.** What did change, and
 * deliberately:
 *
 * - `Dialog` carries a real sticky footer, so the Template modal no longer clips its
 *   own "Add attribute values" row and Download/Cancel buttons off the bottom.
 * - Each `Dialog` gets `closeOnOutsideClick` because `ModalContainer.clickOutSideClose`
 *   defaults to true — the users of this page can already dismiss by clicking beside
 *   the panel and keeping that is not optional. Escape now works too, which it did not.
 * - `ReversePDW` is still rendered TWICE (inside the Product Data dialog and, as
 *   before, permanently on the page — see `okf/pdw/e2e.md` § 7). Both instances now
 *   take an `instanceId`, which scopes their radio-group `name`, their iframe `id`
 *   and their testids. Before this, both groups were literally `name="optradio"`,
 *   so choosing a category in the dialog silently un-checked the page's selection
 *   behind it while React's state still said it was selected.
 */
function PdwManage(props) {
  const [showPopup, setShowPopup] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [showValidatePopup, setShowValidatePopup] = useState(false);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);
  const [showReversePDWPopup, setShowReversePDWPopup] = useState(false);

  const modalOpen = () => setShowPopup(true);
  const modalClose = () => setShowPopup(false);

  const modalLogsOpen = () => setShowLogs(true);
  const modalLogsClose = () => setShowLogs(false);

  const modalValidateOpen = () => setShowValidatePopup(true);
  const modalValidateClose = () => setShowValidatePopup(false);

  const modalCreateOpen = () => setShowCreatePopup(true);
  const modalCreateClose = () => setShowCreatePopup(false);
  const modalUpdateOpen = () => setShowUpdatePopup(true);
  const modalUpdateClose = () => setShowUpdatePopup(false);

  const modalReversePDWOpen = () => setShowReversePDWPopup(true);
  const modalReversePDWClose = () => setShowReversePDWPopup(false);

  const api_base_url = AppSettings.GMS_BACKEND_API_URL;
  let appData = useContext(AppContext);
  const navigate = useNavigate();
  let industryId =
    appData.get_selected_industry() && appData.get_selected_industry().id;
  const [Loading, setLoading] = useState({
    status: "",
    message: "",
  });
  const [categoriesList, setCategoriesList] = useState(null);
  const [categoryTypeList, setCategoryTypeList] = useState(null);

  useEffect(() => {
    if (appData.pm.checkPermission("gmsproducts_read")) {
      if (industryId) fetchData();
    } else {
      navigate("/workspace/module/permission/denied");
    }
  }, [industryId]);

  async function fetchData() {
    setLoading({ status: "Loading", message: "Loading Categories Details" });

    Promise.all([load_categories(), load_category_types()])
      .then((res) => {
        setLoading({ status: "Success", message: undefined });
      })
      .catch((err) => {
        setLoading({
          status: "Error",
          message: err,
        });
      });
  }

  function load_categories() {
    return new Promise(async (resolve, reject) => {
      let res = await ServiceWrapper.doGet(
        `${api_base_url}category/getbyindustryid/${industryId}`,
        null,
        null,
        AuthManager.getGmsToken()
      );

      if (res.errorfound) reject("Error while Fetching categories details");
      if (!res.data.done) reject(res.data.message);

      const categories = await res.data.data.reduce((acc, category) => {
        (acc[category.cattype] = acc[category.cattype] || []).push(category);
        return acc;
      }, {});

      await setCategoriesList(categories);
      resolve();
    });
  }

  function load_category_types() {
    return new Promise(async (resolve, reject) => {
      let res = await ServiceWrapper.doGet(
        `${api_base_url}categorytype/getbyindustryid/${industryId}`,
        null,
        null,
        AuthManager.getGmsToken()
      );

      if (res.errorfound) reject("Error while Fetching categories details");
      if (!res.data.done) reject(res.data.message);

      await setCategoryTypeList(res.data.data);
      resolve();
    });
  }

  /*
    Three states, not two. `categoriesList === null` means the fetch has not landed —
    render nothing rather than claim there are none. Legacy expressed this as
    `categoriesList === null || categoriesList.length === 0 ? <div/> : …`, where the
    `.length` half was always undefined on an object and so never fired; the null half
    is the one that mattered.
  */
  const stillLoadingCategories = categoriesList === null || categoriesList === undefined;
  const hasCategories =
    categoriesList &&
    categoryTypeList &&
    Object.keys(categoriesList).length > 0;

  /*
    The permission gate keeps its shipped shape: the control stays ENABLED and a
    denial raises an info toast. Disabling it instead would be a behaviour change,
    and the toast is what tells the user *why* nothing happened.
  */
  const gated = (allowed, action, message) => () => {
    if (allowed) {
      action();
    } else {
      toast.info(message);
    }
  };

  const readPermission = appData.pm.checkPermission("gmsproducts_read");
  const updatePermission = appData.pm.checkPermission("gmsproducts_update");

  const downloadOptions = [
    { id: "product-template", label: "Product Template" },
    { id: "product-data", label: "Product Data" },
  ];

  const uploadOptions = [
    { id: "product-creation", label: "Product Creation File" },
    { id: "product-modification", label: "Product Modification File" },
  ];

  const onDownloadSelect = (optionId) => {
    if (optionId === "product-template") {
      gated(
        readPermission,
        modalOpen,
        "You don't have permission to download the template"
      )();
      return;
    }
    gated(
      readPermission,
      modalReversePDWOpen,
      "You don't have permission to download product data"
    )();
  };

  const onUploadSelect = (optionId) => {
    if (optionId === "product-creation") {
      gated(
        updatePermission,
        modalCreateOpen,
        "You don't have permission to create the products"
      )();
      return;
    }
    gated(
      updatePermission,
      modalUpdateOpen,
      "You don't have permission to update the products"
    )();
  };

  if (Loading.status === "Loading") {
    return (
      <div data-testid="pdw-page-skeleton" aria-busy="true" aria-live="polite">
        <span className="tw-sr-only">Loading PDW categories</span>
        {/* Header band: title left, four actions right — the shape the real page lands in. */}
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-b tw-border-edge tw-px-4 tw-py-3">
          <Skeleton variant="text" width="80px" />
          <div className="tw-flex tw-items-center tw-gap-3">
            <Skeleton variant="text" width="72px" />
            <Skeleton variant="rect" width="104px" height="36px" />
            <Skeleton variant="rect" width="92px" height="36px" />
            <Skeleton variant="rect" width="92px" height="36px" />
          </div>
        </div>
        {/* Download-Products band: heading + description left, search right. */}
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-border-b tw-border-edge tw-px-4 tw-py-3">
          <div className="tw-flex tw-flex-col tw-gap-2 tw-flex-1 tw-min-w-0">
            <Skeleton variant="text" width="180px" />
            <Skeleton variant="text" width="60%" />
          </div>
          <Skeleton variant="rect" width="260px" height="36px" />
        </div>
        {/* The category grid — four across, the same as the loaded page. */}
        <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-x-6 tw-gap-y-3 tw-px-4 tw-py-4">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((item) => (
            <Skeleton key={item} variant="text" width="70%" />
          ))}
        </div>
        <div className="tw-px-4">
          <Skeleton variant="rect" width="120px" height="36px" />
        </div>
      </div>
    );
  }

  return (
    /* § 271 — `tw-px-0`, not the `tw-px-4` this carried. The shell's own `<main>`
       already applies `tw-px-4`, so this page's content started at viewport 272 where
       Guide, Compatibility, Manage Party and Category List all start at 256 — PDW was
       16px further in than every other page, the fifth distinct inset number in the
       app (§§ 266-268 settled it at 0). One token, because PDW was already internally
       consistent: header, panel and content all share this one wrapper, which is the
       structure §§ 266-267 had to build elsewhere.

       A bare block comment, not a braced JSX one: this is the root of a `return ( … )`,
       where a JSX comment is a second expression and not a comment at all. */
    <div data-testid="pdw-page" className="tw-px-0 tw-pt-2">
      <Dialog
        isOpen={showPopup}
        onClose={modalClose}
        closeOnOutsideClick
        size="lg"
        title="Download - Product Template"
        data-testid="pdw-template-dialog"
      >
        <div className="tw-mb-4">
          <Typography variant="body-primary" as="p" className="tw-text-ink-secondary">
            Choose any category to download blank product template
          </Typography>
        </div>
        {stillLoadingCategories ? null : hasCategories ? (
          <PdwDownload
            categoriesList={categoriesList}
            categoryTypeList={categoryTypeList}
            modalClose={modalClose}
          />
        ) : (
          <Typography variant="body-primary" as="p" data-testid="pdw-template-empty">
            No categories available to select
          </Typography>
        )}
      </Dialog>

      <Dialog
        isOpen={showLogs}
        onClose={modalLogsClose}
        closeOnOutsideClick
        size="lg"
        title="PDW Logs"
        data-testid="pdw-logs-dialog"
      >
        <PDWTracker />
      </Dialog>

      <Dialog
        isOpen={showReversePDWPopup}
        onClose={modalReversePDWClose}
        closeOnOutsideClick
        size="lg"
        title="Download - Product Data"
        data-testid="pdw-product-data-dialog"
      >
        {hasCategories ? (
          /* `instanceId` is what keeps this copy's radio group, iframe id and
             testids distinct from the always-mounted one at the bottom of the page. */
          <ReversePDW
            instanceId="pdw-reversepdw-dialog"
            categoriesList={categoriesList}
            categoryTypeList={categoryTypeList}
          />
        ) : (
          <Typography variant="body-primary" as="p" data-testid="pdw-product-data-empty">
            No categories available to select
          </Typography>
        )}
      </Dialog>

      <Dialog
        isOpen={showValidatePopup}
        onClose={modalValidateClose}
        closeOnOutsideClick
        title="Validate"
        data-testid="pdw-validate-dialog"
      >
        <Validate modalValidateClose={modalValidateClose} />
      </Dialog>

      <Dialog
        isOpen={showCreatePopup}
        onClose={modalCreateClose}
        closeOnOutsideClick
        title="Upload - Product Creation File"
        data-testid="pdw-create-dialog"
      >
        <Create modalCreateClose={modalCreateClose} />
      </Dialog>

      <Dialog
        isOpen={showUpdatePopup}
        onClose={modalUpdateClose}
        closeOnOutsideClick
        title="Upload - Product Modification File"
        data-testid="pdw-update-dialog"
      >
        <Update modalUpdateClose={modalUpdateClose} />
      </Dialog>

      {/*
        Flex, not the old `float:left` / `float:right` pair. The floats needed an
        `overflow-hidden` clearfix on the wrapper, and that clearfix was also a
        clipping ancestor for anything absolutely positioned inside it — which is
        exactly what a dropdown menu is.
      */}
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-b tw-border-edge tw-pb-3 tw-mb-5">
        {/* § 271 — `heading-2` (18px/500). It was `heading-1` (18px/700), preserving the
            legacy `fontSize={1.125} fontWeight="700"` — and it was the LAST name on
            `pageTitleConvention.test.ts`'s allowlist, which is documented "shrinks, never
            grows". Both tokens are 18px and differ only in weight, so a bold title among
            medium ones is invisible on its own screen and only shows up across pages.
            That entry is deleted with this change and the list is now empty. */}
        <Typography className="tw-text-ink" variant="display" as="h1" data-testid="pdw-page-title">
          PDW
        </Typography>

        {/* § 271 — two jobs, not four peers. View Logs opens a log dialog: reference,
            not an operation on the data. It takes the quiet rung (`subtle` — transparent,
            ink, neutral hover; the DS's designated toolbar trigger, § 65/§ 181) and the
            three data operations group behind a 24px break. The row then climbs exactly
            once: quiet text, outlined, outlined, filled.

            No `gap` on this outer group — its two children state their own spacing, so
            the 24px below reads as 24 rather than as 24-plus-whatever-the-row-adds. */}
        <div className="tw-flex tw-items-center">
          <Button
            variant="subtle"
            id="pdw-view-logs-btn"
            data-testid="pdw-view-logs-btn"
            onClick={modalLogsOpen}
          >
            View Logs
          </Button>

          <div id="pdw-header-actions" className="tw-flex tw-items-center tw-gap-3 tw-ms-6 tw-shrink-0">
          <DropdownButton
            label="Download"
            variant="secondary"
            align="right"
            options={downloadOptions}
            onSelect={onDownloadSelect}
            data-testid="pdw-download-btn"
          />

          <Button
            variant="secondary"
            id="pdw-validate-btn"
            data-testid="pdw-validate-btn"
            onClick={gated(
              updatePermission,
              modalValidateOpen,
              "You don't have permission to validate the products"
            )}
          >
            Validate
          </Button>

          <DropdownButton
            label="Upload"
            variant="primary"
            align="right"
            options={uploadOptions}
            onSelect={onUploadSelect}
            data-testid="pdw-upload-btn"
          />
          </div>
        </div>
      </div>

      {Loading.status === "Success" &&
        (hasCategories ? (
          <ReversePDW
            instanceId="pdw-reversepdw"
            categoriesList={categoriesList}
            categoryTypeList={categoryTypeList}
          />
        ) : (
          <Typography variant="body-primary" as="p" data-testid="pdw-page-empty">
            No categories available to select
          </Typography>
        ))}
    </div>
  );
}

export default PdwManage;
